import React, { useState, useRef } from 'react';
import { COLORS, SPACING, FONT_SIZE, BORDER_RADIUS } from '../constants/theme';
import useMediaUpload from '../hooks/useMediaUpload';

/**
 * MediaUploadField Component
 * Reusable file upload field with drag-and-drop support, validation, and preview
 *
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {string} props.accept - Accepted MIME types (comma-separated)
 * @param {number} props.maxSize - Maximum file size in bytes
 * @param {string} props.currentUrl - Current media URL (for display when editing)
 * @param {Function} props.onUpload - Callback when file is uploaded (file) => Promise<{path, url}>
 * @param {Function} props.onRemove - Callback when file is removed
 * @param {'video'|'image'} props.type - Type of media (video or image)
 * @param {boolean} props.disabled - Disable the field
 */
const MediaUploadField = ({
  label,
  accept,
  maxSize,
  currentUrl = null,
  onUpload,
  onRemove,
  type = 'image',
  disabled = false,
}) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(currentUrl);
  const [uploadError, setUploadError] = useState('');

  const { validateFile, uploading, progress, error: uploadHookError } = useMediaUpload();

  const maxSizeMB = Math.round(maxSize / (1024 * 1024));

  /**
   * Handle file selection
   */
  const handleFileSelect = async (file) => {
    if (!file || disabled) return;

    setUploadError('');

    // Validate file
    const validation = validateFile(file, type, maxSize);
    if (!validation.valid) {
      setUploadError(validation.error);
      return;
    }

    // Set selected file and create preview URL for images
    setSelectedFile(file);
    if (type === 'image') {
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
    } else {
      setPreviewUrl(null);
    }

    // Call onUpload callback
    if (onUpload) {
      try {
        const result = await onUpload(file);
        if (result && result.success) {
          setUploadError('');
        } else if (result && result.error) {
          setUploadError(result.error);
          setSelectedFile(null);
          setPreviewUrl(currentUrl);
        }
      } catch (err) {
        setUploadError(err.message);
        setSelectedFile(null);
        setPreviewUrl(currentUrl);
      }
    }
  };

  /**
   * Handle file input change
   */
  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  /**
   * Handle drag events
   */
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  /**
   * Handle remove file
   */
  const handleRemove = async () => {
    if (disabled) return;

    setSelectedFile(null);
    setPreviewUrl(null);
    setUploadError('');

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Call onRemove callback
    if (onRemove) {
      await onRemove();
    }
  };

  /**
   * Trigger file input click
   */
  const handleBrowseClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Styles
  const dropZoneStyle = {
    border: `2px dashed ${isDragging ? COLORS.primary : COLORS.gray[300]}`,
    borderRadius: BORDER_RADIUS.md,
    padding: SPACING.lg,
    textAlign: 'center',
    cursor: disabled ? 'not-allowed' : 'pointer',
    backgroundColor: isDragging ? COLORS.primary + '10' : disabled ? COLORS.gray[50] : COLORS.white,
    transition: 'all 0.2s ease',
    opacity: disabled ? 0.6 : 1,
  };

  const labelStyle = {
    display: 'block',
    marginBottom: SPACING.sm,
    fontSize: FONT_SIZE.md,
    fontWeight: '500',
    color: COLORS.gray[700],
  };

  const errorStyle = {
    color: COLORS.danger,
    fontSize: FONT_SIZE.sm,
    marginTop: SPACING.sm,
  };

  const progressBarContainerStyle = {
    width: '100%',
    height: '8px',
    backgroundColor: COLORS.gray[200],
    borderRadius: BORDER_RADIUS.sm,
    marginTop: SPACING.md,
    overflow: 'hidden',
  };

  const progressBarStyle = {
    height: '100%',
    backgroundColor: COLORS.primary,
    transition: 'width 0.3s ease',
    width: `${progress}%`,
  };

  const previewContainerStyle = {
    marginTop: SPACING.md,
    position: 'relative',
  };

  const previewImageStyle = {
    maxWidth: '200px',
    maxHeight: '200px',
    borderRadius: BORDER_RADIUS.md,
    border: `1px solid ${COLORS.gray[300]}`,
  };

  const fileInfoStyle = {
    marginTop: SPACING.md,
    padding: SPACING.md,
    backgroundColor: COLORS.gray[50],
    borderRadius: BORDER_RADIUS.sm,
    fontSize: FONT_SIZE.sm,
    color: COLORS.gray[700],
  };

  const buttonGroupStyle = {
    display: 'flex',
    gap: SPACING.sm,
    marginTop: SPACING.md,
    justifyContent: 'center',
  };

  const buttonStyle = {
    padding: `${SPACING.sm} ${SPACING.md}`,
    fontSize: FONT_SIZE.sm,
    borderRadius: BORDER_RADIUS.sm,
    cursor: 'pointer',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: COLORS.primary,
    color: COLORS.white,
    border: 'none',
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: COLORS.white,
    color: COLORS.gray[700],
    border: `1px solid ${COLORS.gray[300]}`,
  };

  return (
    <div>
      {label && <label style={labelStyle}>{label}</label>}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        style={{ display: 'none' }}
        disabled={disabled}
      />

      {/* Drop zone */}
      {!selectedFile && !previewUrl ? (
        <div
          style={dropZoneStyle}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={handleBrowseClick}
        >
          <div style={{ fontSize: '48px', marginBottom: SPACING.sm }}>
            {type === 'video' ? '🎥' : '🖼️'}
          </div>
          <p style={{ fontSize: FONT_SIZE.md, color: COLORS.gray[600], margin: 0 }}>
            {isDragging ? 'Drop file here' : 'Drag and drop a file here, or click to browse'}
          </p>
          <p style={{ fontSize: FONT_SIZE.sm, color: COLORS.gray[500], marginTop: SPACING.xs }}>
            {type === 'video'
              ? `MP4, MOV, WebM up to ${maxSizeMB}MB`
              : `JPG, PNG, WebP up to ${maxSizeMB}MB`}
          </p>
        </div>
      ) : (
        <div>
          {/* Preview or file info */}
          <div style={previewContainerStyle}>
            {type === 'image' && previewUrl ? (
              <img src={previewUrl} alt="Preview" style={previewImageStyle} />
            ) : selectedFile ? (
              <div style={fileInfoStyle}>
                <div style={{ fontWeight: '500', marginBottom: SPACING.xs }}>
                  📄 {selectedFile.name}
                </div>
                <div style={{ fontSize: FONT_SIZE.xs, color: COLORS.gray[600] }}>
                  {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                </div>
              </div>
            ) : currentUrl ? (
              <div style={fileInfoStyle}>
                <div style={{ fontWeight: '500' }}>
                  {type === 'video' ? '🎥' : '🖼️'} File uploaded
                </div>
              </div>
            ) : null}
          </div>

          {/* Action buttons */}
          <div style={buttonGroupStyle}>
            <button
              onClick={handleBrowseClick}
              style={secondaryButtonStyle}
              disabled={disabled || uploading}
            >
              Replace
            </button>
            <button
              onClick={handleRemove}
              style={{ ...secondaryButtonStyle, color: COLORS.danger, borderColor: COLORS.danger }}
              disabled={disabled || uploading}
            >
              Remove
            </button>
          </div>
        </div>
      )}

      {/* Upload progress */}
      {uploading && progress > 0 && (
        <div style={progressBarContainerStyle}>
          <div style={progressBarStyle} />
        </div>
      )}

      {/* Error message */}
      {(uploadError || uploadHookError) && (
        <div style={errorStyle}>{uploadError || uploadHookError}</div>
      )}
    </div>
  );
};

export default MediaUploadField;
